import api from './api';

// --- Types ---
export interface Shop {
    id?: number;
    name: string;
    platform: 'amazon' | 'walmart' | 'tiktok';
    region: string;
    seller_id?: string;
    marketplace_id?: string;
    client_id?: string;
    client_secret?: string;
    refresh_token?: string;
    aws_access_key?: string;
    aws_secret_key?: string;
    role_arn?: string;
    status?: boolean;
}

export interface SellerSKU {
    id?: number;
    shop: number;
    shop_name?: string;
    internal_sku: number;
    internal_sku_code?: string;
    sku_code: string;
    asin: string;
    parent_asin?: string;
    fnsku?: string;
    title?: string;
    status: 'active' | 'inactive' | 'archived';
    owner?: number;
    owner_name?: string;
}

export interface Supplier {
    id?: number;
    name: string;
    code?: string;
    contact_name?: string;
    phone?: string;
    email?: string;
    address?: string;
    payment_terms?: string;
    lead_time_days?: number;
    status?: 'active' | 'inactive';
    link?: string;
    monthly_sales_revenue?: number;
    monthly_gross_profit?: number;
    gross_profit_margin?: number;
    total_purchase_amount?: number;
}

export interface SKUSupplier {
    id?: number;
    sku: number;
    supplier: number;
    supplier_name?: string;
    is_primary: boolean;
    supplier_sku?: string;
    purchase_price: number;
    moq: number;
    lead_time_days: number;
    link?: string;
}

export interface Warehouse {
    id?: number;
    name: string;
    code: string;
    type: 'local' | 'fba' | '3pl';
    address?: string;
    is_active?: boolean;
}

export interface InventoryBatch {
    id?: number;
    batch_number: string;
    sku: number;
    sku_code?: string;
    warehouse: number;
    warehouse_name?: string;
    quantity_initial: number;
    quantity_remaining: number;
    unit_cost: number;
    currency: string;
    inbound_date: string;
    created_at?: string;
}

export interface PurchaseOrder {
    id?: number;
    po_number: string;
    supplier: number;
    supplier_name?: string;
    target_warehouse?: number;
    target_warehouse_name?: string;
    status: 'draft' | 'approval' | 'ordered' | 'production' | 'shipped' | 'received' | 'closed' | 'cancelled';
    order_date?: string;
    expected_arrival_date?: string;
    total_amount: number;
    paid_amount: number;
    created_at?: string;
    notes?: string;
    items?: any[];
}

export interface ShipmentBatch {
    id?: number;
    batch_number: string;
    organization?: number;
    logistics_provider?: number;
    logistics_provider_name?: string;
    carrier?: string;
    tracking_number?: string;
    etd?: string;
    eta?: string;
    atd?: string;
    ata?: string;
    status: 'planning' | 'booked' | 'shipped' | 'customs' | 'delivered' | 'closed';
    freight_cost?: number;
    tax_cost?: number;
    other_cost?: number;
    currency?: string;
}

export interface InboundShipment {
    id?: number;
    shipment_id: string;
    shipment_name?: string;
    shop: number;
    shop_name?: string;
    batch?: number;
    batch_number?: string;

    // Logistics
    logistics_provider?: number;
    logistics_provider_name?: string;
    shipping_price?: number;
    carrier_tracking_number?: string;

    // Tracking Dates
    shipment_date?: string;
    estimated_arrival_date?: string;
    actual_arrival_date?: string;
    shelf_date?: string;

    // Details
    logistics_channel?: string;

    // Physical
    total_weight_kg?: number;
    total_volume_cbm?: number;

    shipment_status: 'WORKING' | 'SHIPPED' | 'RECEIVING' | 'CLOSED' | 'CANCELLED' | 'DELETED';
    destination_fulfillment_center_id?: string;
    label_prep_type?: string;
    has_discrepancy?: boolean;
    items?: InboundShipmentItem[];
}

export interface InboundShipmentItem {
    id?: number;
    shipment: number;
    seller_sku: number;
    seller_sku_code?: string;
    seller_sku_name?: string;
    main_image?: string;

    quantity_shipped: number;
    quantity_received: number;
    discrepancy_qty: number;

    unit_purchase_cost?: number;
    unit_weight_kg?: number;

    source_purchase_order?: number;
    source_inventory_batch?: number;
}

export interface LogisticsProvider {
    id?: number;
    name: string;
    code?: string;
    contact_name?: string;
    contact_phone?: string;
    email?: string;
    provider_type: 'freight_forwarder' | 'courier' | 'truck';
    status: 'active' | 'inactive';
}

export interface ReplenishmentConfig {
    id?: number;
    organization?: number;
    seller_sku?: number | SellerSKU; // Can be ID or expanded object
    seller_sku_code?: string;

    target_days_of_inventory: number;
    safety_stock_days: number;
    lead_time_days: number;
    min_restock_qty: number;
}

export interface ReplenishmentSuggestion {
    id?: number;
    seller_sku: number;
    seller_sku_code?: string;
    seller_sku_name?: string;
    main_image?: string;
    generated_at: string;

    avg_daily_sales_7d: number;
    avg_daily_sales_30d: number;
    weighted_velocity: number;

    current_stock: number;
    inbound_stock: number;
    reserved_stock: number;

    days_of_supply: number;
    suggested_restock_qty: number;
    status: 'healthy' | 'restock_now' | 'overstock';
    status_display?: string;

    is_processed: boolean;
    action_taken?: string;
}

export interface FBAInventory {
    id: number;
    seller_sku: number;
    seller_sku_code?: string;
    seller_sku_name?: string;
    shop_name?: string;
    main_image?: string;
    afn_warehouse_quantity: number;
    afn_fulfillable_quantity: number;
    afn_unsellable_quantity: number;
    afn_reserved_quantity: number;
    afn_inbound_working_quantity: number;
    afn_inbound_shipped_quantity: number;
    afn_inbound_receiving_quantity: number;
    inv_age_0_to_90_days: number;
    inv_age_91_to_180_days: number;
    inv_age_181_to_270_days: number;
    inv_age_271_to_365_days: number;
    inv_age_271_to_365_days_plus?: number;
    inv_age_365_plus_days: number;
    estimated_excess_quantity: number;
    report_date: string;
}

export interface ReplenishmentRecommendation {
    id: number;
    sku_code: string;
    product_name: string;
    main_image: string;
    total_stock: number;
    avg_daily_sales: number;
    cover_days: number;
    restock_needed: number;
    status: string;
}

// --- Service ---
const supplyChainService = {
    // Shops
    getShops: () => api.get<Shop[]>('/shops/'),
    createShop: (data: Shop) => api.post<Shop>('/shops/', data),
    updateShop: (id: number, data: Shop) => api.put<Shop>(`/shops/${id}/`, data),
    deleteShop: (id: number) => api.delete(`/shops/${id}/`),

    // Seller SKUs
    getSellerSKUs: (params?: any) => api.get<SellerSKU[]>('/seller-skus/', { params }),
    createSellerSKU: (data: SellerSKU) => api.post<SellerSKU>('/seller-skus/', data),
    updateSellerSKU: (id: number, data: Partial<SellerSKU>) => api.patch<SellerSKU>(`/seller-skus/${id}/`, data),
    deleteSellerSKU: (id: number) => api.delete(`/seller-skus/${id}/`),
    syncFromOrders: () => api.post<{ success: boolean; message: string; created_count: number; linked_orders_count: number }>('/seller-skus/sync_from_orders/'),
    exportExcel: () => api.get('/seller-skus/export_excel/', { responseType: 'blob' }),
    batchImport: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post<{ success: boolean; message: string; created_count: number; updated_count: number; error_rows: any[] }>('/seller-skus/batch_import/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },

    // Suppliers
    getSuppliers: (params?: any) => api.get<Supplier[]>('/suppliers/', { params }),
    getSupplierById: (id: number) => api.get<Supplier>(`/suppliers/${id}/`),
    createSupplier: (data: Supplier) => api.post<Supplier>('/suppliers/', data),
    updateSupplier: (id: number, data: Supplier) => api.put<Supplier>(`/suppliers/${id}/`, data),
    deleteSupplier: (id: number) => api.delete(`/suppliers/${id}/`),

    // SKU Suppliers
    getSKUSuppliers: (params?: any) => api.get<SKUSupplier[]>('/sku-suppliers/', { params }),
    createSKUSupplier: (data: SKUSupplier) => api.post<SKUSupplier>('/sku-suppliers/', data),
    updateSKUSupplier: (id: number, data: Partial<SKUSupplier>) => api.patch<SKUSupplier>(`/sku-suppliers/${id}/`, data),
    deleteSKUSupplier: (id: number) => api.delete(`/sku-suppliers/${id}/`),

    // Warehouses
    getWarehouses: (params?: any) => api.get<Warehouse[]>('/warehouses/', { params }),
    createWarehouse: (data: Warehouse) => api.post<Warehouse>('/warehouses/', data),
    updateWarehouse: (id: number, data: Partial<Warehouse>) => api.patch<Warehouse>(`/warehouses/${id}/`, data),
    deleteWarehouse: (id: number) => api.delete(`/warehouses/${id}/`),

    // Inventory Batches
    getInventoryBatches: (params?: any) => api.get<InventoryBatch[]>('/inventory-batches/', { params }),

    // Purchase Orders
    getPurchaseOrders: (params?: any) => api.get<PurchaseOrder[]>('/purchase-orders/', { params }),
    getPurchaseOrder: (id: number) => api.get<PurchaseOrder>(`/purchase-orders/${id}/`),
    createPurchaseOrder: (data: PurchaseOrder) => api.post<PurchaseOrder>('/purchase-orders/', data),
    updatePurchaseOrder: (id: number, data: Partial<PurchaseOrder>) => api.patch<PurchaseOrder>(`/purchase-orders/${id}/`, data),
    deletePurchaseOrder: (id: number) => api.delete(`/purchase-orders/${id}/`),
    receivePurchaseOrder: (id: number) => api.post(`/purchase-orders/${id}/receive/`),
    approvePurchaseOrder: (id: number) => api.post(`/purchase-orders/${id}/approve/`),
    generatePurchaseOrders: (items: { seller_sku_code: string; quantity: number }[]) =>
        api.post<{ generated_ids: number[], count: number }>('/purchase-orders/auto_generate/', { items }),

    // Logistics (Batches)
    getShipmentBatches: () => api.get<ShipmentBatch[]>('/shipment-batches/'),
    createShipmentBatch: (data: ShipmentBatch) => api.post<ShipmentBatch>('/shipment-batches/', data),
    updateShipmentBatch: (id: number, data: Partial<ShipmentBatch>) => api.patch<ShipmentBatch>(`/shipment-batches/${id}/`, data),

    // Inbound Shipments
    getInboundShipments: (params?: any) => api.get<InboundShipment[]>('/inbound-shipments/', { params }),
    createInboundShipment: (data: InboundShipment) => api.post<InboundShipment>('/inbound-shipments/', data),
    updateInboundShipment: (id: number, data: Partial<InboundShipment>) => api.patch<InboundShipment>(`/inbound-shipments/${id}/`, data),

    // FBA Inventory
    getFBAInventory: (params?: any) => api.get<FBAInventory[]>('/fba-inventory/', { params }),
    getInventoryAgeDistribution: (params?: any) => api.get<any>('/fba-inventory/age_distribution/', { params }),
    getReplenishmentAdvice: (params?: { target_days?: number, sales_days?: number, shop?: number }) =>
        api.get<ReplenishmentRecommendation[]>('/fba-inventory/replenishment/', { params }),

    // Logistics Providers
    getLogisticsProviders: (params?: any) => api.get<LogisticsProvider[]>('/logistics-providers/', { params }),
    createLogisticsProvider: (data: LogisticsProvider) => api.post<LogisticsProvider>('/logistics-providers/', data),
    updateLogisticsProvider: (id: number, data: LogisticsProvider) => api.put<LogisticsProvider>(`/logistics-providers/${id}/`, data),
    deleteLogisticsProvider: (id: number) => api.delete(`/logistics-providers/${id}/`),

    // Replenishment
    getReplenishmentConfigs: (params?: any) => api.get<ReplenishmentConfig[]>('/replenishment-configs/', { params }),
    createReplenishmentConfig: (data: ReplenishmentConfig) => api.post<ReplenishmentConfig>('/replenishment-configs/', data),
    updateReplenishmentConfig: (id: number, data: ReplenishmentConfig) => api.patch<ReplenishmentConfig>(`/replenishment-configs/${id}/`, data),

    getReplenishmentSuggestions: (params?: any) => api.get<ReplenishmentSuggestion[]>('/replenishment-suggestions/', { params }),
    generateReplenishmentSuggestions: () => api.post<{ message: string }>('/replenishment-suggestions/generate/'),
};

export default supplyChainService;
